// Chart screenshots are rendered by the Arzdigital widget service (GET /api/screenshot).
// Its public share.js supplies the URL contract (ArzWidget.screenshotUrl); the PDP fetches the
// image once, then copies it (desktop) and offers the OS share sheet and saving.
const WIDGET_SHARE_SCRIPT_URL = 'https://widget.arzdigital.com/share.js';
const SCREENSHOT_TIMEOUT_MS = 30000;

// Highcharts range-button text -> widget `range` parameter.
const WIDGET_RANGES = Object.freeze({
  '24h': '1d',
  '1w': '7d',
  '1m': '1m',
  '3m': '3m',
  '1y': '1y',
  ALL: 'all',
});

const SCREENSHOT_TEXT = Object.freeze({
  title: 'تصویر نمودار',
  loading: 'در حال آماده سازی تصویر…',
  ready: 'تصویر آماده است.',
  copied: 'تصویر کپی شد. می توانید آن را ذخیره یا به اشتراک بگذارید.',
  saved: 'تصویر ذخیره شد.',
  error: 'دریافت تصویر نمودار ممکن نشد.',
});

// Mirrors the chart at click time: selected interval, تومانی/دلاری currency, and the
// server's user-agent device class (desktop 16:9, mobile 9:16). The widget only renders
// price charts, so metric views have no matching screenshot.
function getChartScreenshotOptions(state) {
  const id = Number(state?.id);
  if (!Number.isInteger(id) || id < 1) return null;
  if (state?.metric && state.metric !== 'price') return null;
  return {
    id,
    aspectRatio: state?.isMobile ? '9:16' : '16:9',
    range: WIDGET_RANGES[state?.range] || '1d',
    currency: state?.currency === 'irt' ? 'irt' : 'usd',
    format: 'png',
  };
}

function getChartScreenshotFileName(state, options) {
  const slug = String(state?.slug || '').toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '');
  return `arzdigital-${slug || options.id}-${options.range}-${options.currency}.png`;
}

function getChartScreenshotTitle(state) {
  return state?.name ? `نمودار قیمت ${state.name} | ارزدیجیتال` : 'نمودار قیمت | ارزدیجیتال';
}

function fetchChartScreenshot(fetchImpl, url, timeoutMs = SCREENSHOT_TIMEOUT_MS) {
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
  const settle = (value) => { clearTimeout(timer); return value; };
  return fetchImpl(url, { cache: 'no-store', signal: controller ? controller.signal : undefined })
    .then((response) => {
      if (!response.ok) throw new Error(`Screenshot failed (HTTP ${response.status})`);
      return response.blob();
    })
    .then((blob) => {
      if (!/^image\//.test(blob.type)) throw new Error('Screenshot response is not an image');
      return settle(blob);
    }, (error) => { settle(); throw error; });
}

// Called inside the click: ClipboardItem accepts the pending image, keeping the user gesture.
function copyImageToClipboard(win, blobPromise) {
  try {
    const clipboard = win.navigator?.clipboard;
    if (!(win.isSecureContext && clipboard?.write && typeof win.ClipboardItem === 'function')) return Promise.resolve(false);
    return clipboard.write([new win.ClipboardItem({ 'image/png': blobPromise })]).then(() => true, () => false);
  } catch (_error) {
    return Promise.resolve(false);
  }
}

function canShareImageFile(nav, file) {
  try {
    return Boolean(nav?.share && nav.canShare && nav.canShare({ files: [file] }));
  } catch (_error) {
    return false;
  }
}

// Nothing is requested until the first call: pages only warm it on user intent.
function createWidgetShareLoader(doc, win, src = WIDGET_SHARE_SCRIPT_URL) {
  let pending = null;
  return {
    get() {
      return win.ArzWidget && typeof win.ArzWidget.screenshotUrl === 'function' ? win.ArzWidget : null;
    },
    load() {
      const loaded = this.get();
      if (loaded) return Promise.resolve(loaded);
      if (pending) return pending;
      pending = new Promise((resolve, reject) => {
        const script = doc.createElement('script');
        // A failed load can be retried by the next click.
        const fail = () => {
          script.remove();
          pending = null;
          reject(new Error('Widget share script unavailable'));
        };
        script.src = src;
        script.async = true;
        script.onload = () => {
          const widget = this.get();
          if (widget) resolve(widget);
          else fail();
        };
        script.onerror = fail;
        doc.head.appendChild(script);
      });
      return pending;
    },
  };
}

module.exports = {
  SCREENSHOT_TEXT,
  WIDGET_RANGES,
  WIDGET_SHARE_SCRIPT_URL,
  canShareImageFile,
  copyImageToClipboard,
  createWidgetShareLoader,
  fetchChartScreenshot,
  getChartScreenshotFileName,
  getChartScreenshotOptions,
  getChartScreenshotTitle,
};
